/*
 * GALPÓN DE DISEÑO — Swipe-to-close de los drawers (mobile)
 * ---------------------------------------------------------------------------
 * Gesto natural: deslizar el drawer hacia su borde para cerrarlo.
 *   - MENÚ   (.menu-drawer, abre desde la IZQUIERDA) → swipe IZQUIERDA cierra.
 *   - CARRITO (.cart-drawer__dialog, abre desde la DERECHA) → swipe DERECHA cierra.
 *
 * Mecánica premium (sin "rebote"):
 *   1. El dedo arrastra EL PANEL (el propio dialog/drawer, no un hijo transparente) → detrás
 *      queda el scrim/página real, no un bloque de color.
 *   2. Al soltar:
 *        · pasó el umbral → CONTINÚA desde donde quedó el dedo hasta salir de pantalla
 *          (transición suave), y SOLO ahí ejecuta el cierre real (ya invisible → sin rebote).
 *        · no pasó → vuelve a su lugar (snap-back suave).
 * Direction-lock para no chocar con el scroll vertical del contenido.
 *
 * A prueba de fallos: si un drawer no está, se omite. Defer: no bloquea.
 */
(function () {
  'use strict';

  const CLOSE_THRESHOLD = 70; // px de arrastre para gatillar el cierre
  const DIRECTION_LOCK = 10; // px para decidir horizontal vs vertical
  const RELEASE_MS = 240; // duración de la animación al soltar
  const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  /**
   * @param {object} cfg
   * @param {() => boolean} cfg.isOpen
   * @param {() => HTMLElement|null} cfg.panel  — el elemento que se MUEVE (se relee por gesto)
   * @param {1|-1} cfg.dir                      — dirección de cierre (+1 derecha, -1 izquierda)
   * @param {HTMLElement} cfg.listenOn          — dónde escuchar el touch (estable)
   * @param {() => void} cfg.close              — cierre real (cuando el panel ya salió de pantalla)
   */
  function attachSwipe(cfg) {
    let startX = 0, startY = 0, dx = 0;
    let dragging = false, decided = false;
    let panel = null;

    function clearStyles(p) {
      if (!p) return;
      p.style.transition = '';
      p.style.transform = '';
      p.style.animation = '';
    }

    function onStart(e) {
      if (!cfg.isOpen() || e.touches.length !== 1) return;
      panel = cfg.panel();
      if (!panel) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dragging = false; decided = false; dx = 0;
    }

    function onMove(e) {
      if (!panel || !cfg.isOpen()) return;
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;

      if (!decided) {
        if (Math.abs(diffX) < DIRECTION_LOCK && Math.abs(diffY) < DIRECTION_LOCK) return;
        decided = true;
        dragging = (cfg.dir < 0 ? diffX < 0 : diffX > 0) && Math.abs(diffX) > Math.abs(diffY);
        if (dragging) {
          // El dialog-drawer corre una `animation: ... forwards` que PINA el transform final
          // y le gana a nuestro transform inline → sin esto, el panel no se movía visualmente.
          // La desactivamos mientras arrastramos para que el dedo SÍ mueva el panel.
          panel.style.animation = 'none';
          panel.style.transition = 'none';
        }
      }
      if (!dragging) return;

      // seguir el dedo SOLO en la dirección de cierre
      dx = cfg.dir < 0 ? Math.min(0, diffX) : Math.max(0, diffX);
      panel.style.transform = 'translateX(' + dx + 'px)';
      e.preventDefault();
    }

    function onEnd() {
      const p = panel;
      const movedBy = dx;
      const wasDragging = dragging;
      dragging = false; decided = false; dx = 0; panel = null;
      if (!wasDragging || !p) { clearStyles(p); return; }

      if (Math.abs(movedBy) >= CLOSE_THRESHOLD) {
        // CONTINÚA hasta salir de pantalla, y recién ahí cierra (ya invisible → sin rebote).
        const offX = cfg.dir < 0 ? -p.offsetWidth - 40 : p.offsetWidth + 40;
        p.style.transition = 'transform ' + RELEASE_MS + 'ms ' + EASE;
        // forzar reflow para que la transición tome el valor de partida actual
        void p.offsetWidth;
        p.style.transform = 'translateX(' + offX + 'px)';
        let done = false;
        const finish = () => {
          if (done) return; done = true;
          p.removeEventListener('transitionend', finish);
          // El panel YA está fuera de pantalla. Cerramos SIN restaurar transform/animation
          // todavía (restaurar acá lo re-pinearía a 0 = el "rebote"). cfg.close() oculta el
          // dialog; recién cuando ya está cerrado limpiamos los estilos para la próxima apertura.
          cfg.close();
          setTimeout(() => clearStyles(p), 60);
        };
        p.addEventListener('transitionend', finish);
        setTimeout(finish, RELEASE_MS + 80); // red de seguridad si transitionend no dispara
      } else {
        // snap-back suave a su lugar
        p.style.transition = 'transform ' + RELEASE_MS + 'ms ' + EASE;
        void p.offsetWidth;
        p.style.transform = 'translateX(0px)';
        const cleanup = () => { p.removeEventListener('transitionend', cleanup); clearStyles(p); };
        p.addEventListener('transitionend', cleanup);
        setTimeout(cleanup, RELEASE_MS + 80);
      }
    }

    cfg.listenOn.addEventListener('touchstart', onStart, { passive: true });
    cfg.listenOn.addEventListener('touchmove', onMove, { passive: false });
    cfg.listenOn.addEventListener('touchend', onEnd, { passive: true });
    cfg.listenOn.addEventListener('touchcancel', onEnd, { passive: true });
  }

  // ── MENÚ ──────────────────────────────────────────────────────────────────
  const menuContainer = document.querySelector('#Details-menu-drawer-container');
  const menuDrawer = menuContainer && menuContainer.querySelector('.menu-drawer');
  if (menuContainer && menuDrawer) {
    menuDrawer.style.touchAction = 'pan-y';
    attachSwipe({
      isOpen: () => menuContainer.hasAttribute('open'),
      panel: () => menuDrawer,
      dir: -1,
      listenOn: menuDrawer,
      close: () => {
        const btn = menuDrawer.querySelector('.menu-drawer__close-button');
        if (btn) btn.click(); else menuContainer.removeAttribute('open');
      },
    });
  }

  // ── CARRITO ───────────────────────────────────────────────────────────────
  // El <dialog> se abre con showModal() → vive en el TOP LAYER; hay que escuchar SOBRE EL
  // DIALOG (no el component) o el touch solo llega en el borde (zona back-gesture del SO).
  // MOVEMOS EL DIALOG ENTERO (es el panel crema): detrás queda el ::backdrop/scrim y la página
  // real, no un bloque de color. Al soltar, la animación nativa `move-and-fade` se desactiva
  // (--dialog-drawer-closing-animation:none) para que NO se sume a la nuestra (eso causaba el
  // "rebote": el panel volvía a 0 y recién ahí animaba).
  const cartComp = document.querySelector('cart-drawer-component');
  const cartDialog = cartComp && cartComp.querySelector('dialog.cart-drawer__dialog, .cart-drawer__dialog');
  if (cartComp && cartDialog) {
    cartDialog.style.touchAction = 'pan-y';
    attachSwipe({
      isOpen: () => cartDialog.open,
      panel: () => cartDialog,
      dir: 1,
      listenOn: cartDialog,
      close: () => {
        // desactivar la animación nativa de cierre SOLO para este cierre-por-swipe (el panel
        // ya salió de pantalla con NUESTRA transición) → sin doble animación / rebote.
        cartDialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
        const restore = () => cartDialog.style.removeProperty('--dialog-drawer-closing-animation');
        const btn = cartDialog.querySelector('[aria-label*="errar"], .close-button, .cart-drawer__close-button');
        if (btn) btn.click();
        else if (typeof cartComp.close === 'function') cartComp.close();
        // restaurar para que la PRÓXIMA apertura/cierre normal (botón X, click fuera) anime bien
        setTimeout(restore, 80);
      },
    });
  }
})();
